import React from 'react';
import {
  BarChart3, Users, UtensilsCrossed, Banknote, TrendingUp, School, CalendarRange
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import LoadingSpinner from '../LoadingSpinner';
import { formatMontant, formatNumber } from './cantineUtils';

const PERIODE_OPTIONS = [
  { value: 'semaine', label: 'Cette semaine' },
  { value: 'mois', label: 'Ce mois' },
  { value: 'trimestre', label: '3 derniers mois' }
];

const NOMS_JOURS_COURTS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

// Histogramme simple des repas servis par jour -- pas de librairie externe,
// une barre proportionnelle au maximum de la periode suffit ici.
const FrequentationChart = ({ parJour }) => {
  if (!parJour.length) {
    return (
      <div className="text-center py-10 bg-white rounded-lg border-2 border-dashed border-gray-200">
        <BarChart3 size={40} className="mx-auto text-gray-400 mb-3" />
        <p className="text-gray-600 font-medium">Aucun repas pointé sur la période</p>
        <p className="text-gray-500 text-sm mt-1">La courbe se remplira dès les premiers pointages</p>
      </div>
    );
  }

  const maxPresents = Math.max(...parJour.map((j) => j.presents), 1);

  return (
    <div className="overflow-x-auto">
      <div className="flex items-end gap-2 min-w-full h-48 px-1">
        {parJour.map((jour) => {
          const date = new Date(`${jour.date_service}T12:00:00`);
          const hauteur = Math.max(4, Math.round((jour.presents / maxPresents) * 100));

          return (
            <div key={jour.date_service} className="flex-1 min-w-[42px] flex flex-col items-center justify-end h-full">
              <span className="text-xs font-semibold text-gray-700 mb-1">{jour.presents}</span>
              <div
                className="w-full rounded-t-md bg-[#246990] hover:bg-[#3084b5] transition-colors"
                style={{ height: `${hauteur}%` }}
                title={`${jour.presents} repas servis · ${jour.absents} absence(s)`}
              />
              <span className="text-[11px] text-gray-500 mt-1.5">
                {NOMS_JOURS_COURTS[date.getDay()]}
              </span>
              <span className="text-[11px] text-gray-400">
                {String(date.getDate()).padStart(2, '0')}/{String(date.getMonth() + 1).padStart(2, '0')}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CantineTableauBordSection = ({
  isLoading,
  data,
  periode,
  setPeriode,
  onRefresh
}) => {
  const effectif = data?.effectif || {};
  const frequentation = data?.frequentation || {};
  const recettes = data?.recettes_ponctuelles || {};
  const parJour = data?.par_jour || [];
  const parClasse = data?.par_classe || [];

  return (
    <div className="space-y-4">
      {/* Sélecteur de période */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <CalendarRange size={20} className="text-[#246990]" />
              <div>
                <p className="text-sm font-semibold text-gray-900">Période analysée</p>
                {data?.periode && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Du {new Date(`${data.periode.date_debut}T12:00:00`).toLocaleDateString('fr-FR')}
                    {' au '}
                    {new Date(`${data.periode.date_fin}T12:00:00`).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Select value={periode} onValueChange={setPeriode}>
                <SelectTrigger className="w-44 bg-white border-gray-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIODE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={onRefresh}>Actualiser</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner message="Chargement du tableau de bord..." color="#246990" />
        </div>
      ) : (
        <>
          {/* Cartes de synthèse */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <Card className="bg-blue-50 border-2 border-blue-200">
              <CardContent className="p-5">
                <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Users size={22} className="text-blue-600" />
                </div>
                <p className="text-gray-600 text-sm mb-3 font-semibold">Effectif inscrit</p>
                <div className="space-y-2 bg-white rounded-lg p-3 border border-blue-100">
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Forfait</span>
                    <span className="font-semibold text-blue-600">{formatNumber(effectif.forfait)}</span>
                  </div>
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Occasionnels</span>
                    <span className="font-semibold text-purple-600">{formatNumber(effectif.occasionnel)}</span>
                  </div>
                  <div className="border-t border-blue-200 pt-2 mt-2 flex justify-between items-center gap-3">
                    <span className="text-gray-700 font-semibold">Total inscrits</span>
                    <span className="text-xl font-bold text-blue-700">{formatNumber(effectif.total)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-green-50 border-2 border-green-200">
              <CardContent className="p-5">
                <div className="w-11 h-11 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <UtensilsCrossed size={22} className="text-green-600" />
                </div>
                <p className="text-gray-600 text-sm mb-3 font-semibold">Repas servis</p>
                <div className="space-y-2 bg-white rounded-lg p-3 border border-green-100">
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Jours servis</span>
                    <span className="font-semibold text-gray-700">{formatNumber(frequentation.jours_servis)}</span>
                  </div>
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Absences</span>
                    <span className="font-semibold text-orange-600">{formatNumber(frequentation.absences)}</span>
                  </div>
                  <div className="border-t border-green-200 pt-2 mt-2 flex justify-between items-center gap-3">
                    <span className="text-gray-700 font-semibold">Total servis</span>
                    <span className="text-xl font-bold text-green-600">{formatNumber(frequentation.repas_servis)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-purple-50 border-2 border-purple-200">
              <CardContent className="p-5">
                <div className="w-11 h-11 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp size={22} className="text-purple-600" />
                </div>
                <p className="text-gray-600 text-sm mb-3 font-semibold">Prévision de couverts</p>
                <div className="space-y-2 bg-white rounded-lg p-3 border border-purple-100">
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Taux de présence</span>
                    <span className="font-semibold text-green-600">{frequentation.taux_presence || 0}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-purple-500"
                      style={{ width: `${frequentation.taux_presence || 0}%` }}
                    />
                  </div>
                  <div className="border-t border-purple-200 pt-2 mt-2">
                    <div className="flex justify-between items-center gap-3">
                      <span className="text-gray-700 font-semibold">Moyenne / jour</span>
                      <span className="text-xl font-bold text-purple-700">
                        {formatNumber(frequentation.moyenne_couverts_par_jour)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Base de calcul pour préparer les repas.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-orange-50 border-2 border-orange-200">
              <CardContent className="p-5">
                <div className="w-11 h-11 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Banknote size={22} className="text-orange-600" />
                </div>
                <p className="text-gray-600 text-sm mb-3 font-semibold">Recettes repas ponctuels</p>
                <div className="space-y-2 bg-white rounded-lg p-3 border border-orange-100">
                  <div className="flex justify-between items-center gap-3 text-sm">
                    <span className="text-gray-600">Repas payés</span>
                    <span className="font-semibold text-gray-700">{formatNumber(recettes.nombre)}</span>
                  </div>
                  <div className="border-t border-orange-200 pt-2 mt-2">
                    <div className="flex justify-between items-center gap-3">
                      <span className="text-gray-700 font-semibold">Encaissé</span>
                      <span className="text-xl font-bold text-orange-600">{formatMontant(recettes.montant)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Hors abonnements forfait, facturés avec la scolarité.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Courbe de fréquentation */}
          <Card>
            <CardHeader>
              <div>
                <CardTitle className="text-xl text-[#246990] flex items-center gap-2">
                  <BarChart3 size={24} />
                  Fréquentation jour par jour
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  nombre de repas effectivement servis sur la période
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <FrequentationChart parJour={parJour} />
            </CardContent>
          </Card>

          {/* Répartition par classe */}
          <Card>
            <CardHeader>
              <div>
                <CardTitle className="text-xl text-purple-600 flex items-center gap-2">
                  <School size={24} />
                  Fréquentation par classe
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  inscrits et repas réellement servis, classe par classe
                </p>
              </div>
            </CardHeader>
            <CardContent>
              {parClasse.length === 0 ? (
                <div className="text-center py-10 bg-white rounded-lg border-2 border-dashed border-gray-200">
                  <School size={40} className="mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600 font-medium">Aucune classe concernée</p>
                  <p className="text-gray-500 text-sm mt-1">Inscrivez des élèves pour voir la répartition</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-purple-100 border-b border-purple-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-purple-900 uppercase">Classe</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-purple-900 uppercase">Inscrits</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-purple-900 uppercase">Repas servis</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-purple-900 uppercase">Absences</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-purple-900 uppercase">Moyenne / jour</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-purple-100">
                      {parClasse.map((classe) => (
                        <tr key={classe.classe_id} className="hover:bg-purple-50 transition-colors">
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-gray-900">{classe.classe_nom}</p>
                            <p className="text-xs text-gray-500">{classe.section_nom || ''}</p>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <p className="text-sm font-semibold text-blue-700">{formatNumber(classe.inscrits)}</p>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <p className="text-sm font-semibold text-green-600">{formatNumber(classe.repas_servis)}</p>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <p className="text-sm text-orange-600">{formatNumber(classe.absences)}</p>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <p className="text-sm font-bold text-purple-700">{formatNumber(classe.moyenne_par_jour)}</p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default CantineTableauBordSection;
